import React, { useEffect, useState } from 'react'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import Moment from 'moment'

import { Conditional } from '@resurs/react-utils'
import { Button, Column, Columns, Container } from '@resurs/midas-react'
import { Cookies } from 'react-cookie'
import { actOnData } from '../../../../util/helpers'

import NewCardOrAccount from './NewCardOrAccount'
import ExistingCardOrAccount from './ExistingCardOrAccount'
import Invoice from './Invoice'
import DisabledMethod from './DisabledMethod'
import PaymentMethodPicker from './PaymentMethodPicker/PaymentMethodPicker'
import PendingApplications from './PendingApplications'
import PendingApplicationCoCustomer from './PendingApplicationCoCustomer'

import {
  getCoApplicantInfo,
  getPaymentPerMonth,
  initialCheckPaymentMethodCostAlternatives,
  resetCurrentPaymentMethod,
  selectPaymentMethod,
  selectPendingPaymentMethod,
  setPeriodOfRepayment,
  validateExternalId,
} from '../../../../store/actions/pay-apply-actions'
import { hasBonusCheckSupport } from '../../../../store/actions/bonus-check-actions'
import { sendPriceInfoMail } from '../../../../store/actions/price-info-mail-actions'
import {
  isExistingPartPayment,
  PaymentMethods,
  PendingApplicationChannel,
} from '../../../../models/payment-methods'
import PageWrapper from '../../../../internal/PageWrapper'
import { getText } from '../../../../internal/TextLabels'
import { CustomerTypeModel } from '../../models/private-models'
import { getBasisConfirmationPdf } from '../../../../store/actions/basis-actions'
import PrintModalUI from '../shared/PrintModalUI'
import ErrorPage from '../ErrorPage'
import { logout } from '../../../../store/actions/auth-actions'
import { useOnMount, useOnUpdate } from '../../../../util/utility-hooks'
import { sendPaymentOptionEvent } from '../../../../util/ga-helpers'
import PspPaymentMethod from './PspPaymentMethod'
import { genericError } from '../../../../store/actions/action-utils'

const getPendingApplicationLabel = ({
  debitableAmount,
  approvedAmount,
} = {}) => {
  if (debitableAmount) {
    return approvedAmount
      ? 'valueLabelWithPayment'
      : 'valueLabelRequestedAmountWithPayment'
  }
  if (approvedAmount) {
    return 'valueLabel'
  }
  return 'valueLabelRequestedAmount'
}

const getDefaultAccountNumber = ({ accounts } = {}) =>
  actOnData(accounts, _accounts => {
    return (_accounts[0] || {}).accountNumber
  }) || null

const hasPendingApplications = ({ pendingApplications } = {}) =>
  pendingApplications &&
  pendingApplications.applications &&
  pendingApplications.applications.length

const getFirstPendingApplication = ({ pendingApplications } = {}) =>
  hasPendingApplications({ pendingApplications })
    ? pendingApplications.applications[0]
    : null

const hasPaymentMethods = ({ paymentMethods } = {}) =>
  paymentMethods && paymentMethods.length

const getFirstPaymentMethod = ({ paymentMethods } = {}) =>
  hasPaymentMethods({ paymentMethods }) ? paymentMethods[0] : null

const hasAccounts = ({ paymentMethods } = {}) =>
  hasPaymentMethods({ paymentMethods }) &&
  paymentMethods[0] &&
  paymentMethods[0].accounts &&
  paymentMethods[0].accounts.length

const getFirstAccount = ({ paymentMethods } = {}) =>
  hasAccounts({ paymentMethods }) ? paymentMethods[0].accounts[0] : null

const hasOneAccount = ({ paymentMethods } = {}) =>
  hasPaymentMethods({ paymentMethods }) &&
  paymentMethods[0] &&
  paymentMethods[0].accounts &&
  paymentMethods[0].accounts.length === 1

const getOneAccount = ({ paymentMethods } = {}) =>
  hasOneAccount({ paymentMethods }) ? paymentMethods[0].accounts[0] : null

const initialDebitCheck = (data, debitEnabledDefault) => {
  const isPaymentCheckSetInCookie =
    new Cookies().get('debit-payment-checked') === 'true'
  return hasPendingApplications(data)
    ? debitEnabledDefault || isPaymentCheckSetInCookie
    : hasPaymentMethods(data) &&
        (isExistingPartPayment(data.paymentMethods[0]) ||
          debitEnabledDefault ||
          isPaymentCheckSetInCookie)
}

const PaymentMethodsPage = props => {
  const [amount, setAmount] = useState(0)
  const [externalIdValid, setExternalIdValid] = useState(true)
  const [newCreditLimitChecked, setNewCreditLimitChecked] = useState(false)
  const [refreshPaymentMethods, setRefreshPaymentMethods] = useState(false)
  const [valid, setValid] = useState(false)
  const [limit, setLimit] = useState(0)
  useEffect(() => setLimit(0), [newCreditLimitChecked])
  const [debitReference, setDebitReference] = useState('')
  const [pdfUrl, setPdfUrl] = useState(null)
  const [order, setOrder] = useState(null)
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(
    hasPendingApplications(props.data)
      ? null
      : getFirstPaymentMethod(props.data),
  )
  const [card, setCard] = useState(
    hasPendingApplications(props.data) ? null : getFirstAccount(props.data),
  )
  const [selectedCard, setSelectedCard] = useState(
    hasPendingApplications(props.data) ? null : getOneAccount(props.data),
  )
  const [pendingApplications, setPendingApplications] = useState(
    hasPendingApplications(props.data),
  )
  const [selectedPendingApplication, setSelectedPendingApplication] = useState(
    getFirstPendingApplication(props.data),
  )
  useEffect(
    () => props.selectPendingPaymentMethod(selectedPendingApplication),
    [selectedPendingApplication],
  )
  const [accountNumber, setAccountNumber] = useState(
    hasPendingApplications(props.data)
      ? null
      : (getFirstAccount(props.data) || {}).accountNumber || null,
  )

  const [debitPaymentChecked, setDebitPaymentChecked] = useState(
    props.debitDisabled
      ? false
      : initialDebitCheck(props.data, props.debitEnabledDefault),
  )

  const ApplicationChannel = new PendingApplicationChannel()

  useOnMount(() => {
    if (!props.data || !props.data.paymentMethods) {
      return
    }

    if (card && card.coApplicant) {
      props.getCoApplicantInfo(card.coApplicant)
    }

    props.getHasBonusSupport(props.governmentId, props.customerType)
    props.resetCurrentPaymentMethod()
    props.selectPendingPaymentMethod(selectedPendingApplication)
    props.selectPaymentMethod({
      amount,
      externalIdValid,
      newCreditLimitChecked,
      refreshPaymentMethods,
      valid,
      limit,
      debitReference,
      pdfUrl,
      order,
      selectedPaymentMethod,
      card,
      selectedCard,
      pendingApplications,
      selectedPendingApplication,
      accountNumber,
      debitPaymentChecked,
    })
    sendPaymentOptionEvent(selectedPaymentMethod, 'Preselected')
  })

  const handleNext = onNext => {
    if (
      valid ||
      onNext.refreshPaymentMethods === true ||
      (order && order.orderId && order.totalAmount) ||
      selectedPendingApplication
    ) {
      setRefreshPaymentMethods(onNext.refreshPaymentMethods)

      props.onNext({
        amount,
        externalIdValid,
        newCreditLimitChecked,
        refreshPaymentMethods: onNext.refreshPaymentMethods,
        valid,
        limit,
        debitReference,
        pdfUrl,
        order,
        selectedPaymentMethod,
        card,
        selectedCard,
        pendingApplications,
        selectedPendingApplication,
        accountNumber,
        debitPaymentChecked,
        analyticsData: {
          dimension8: selectedPaymentMethod || selectedPendingApplication,
          debitPaymentChecked,
        },
      })
    }

    if (!debitPaymentChecked && selectedCard) {
      setSelectedCard(null)
    }
  }

  const handleNextInvoice = onNext => {
    if (order.orderId) {
      props.validateExternalId(order.orderId).then(data => {
        setValid(data.valid)
        setExternalIdValid(data.valid)
        if (data.valid) {
          handleNext(onNext)
        } else {
          genericError()
        }
      })
    } else {
      handleNext(onNext)
    }
  }

  const onOrderChanged = orderData => {
    const _externalIdValid =
      externalIdValid || orderData.orderId !== order.orderId
    setOrder(orderData)
    setExternalIdValid(_externalIdValid)
  }

  const onChangeAmount = _amount => {
    setAmount(Number(_amount))
  }

  const onChangeDebitReference = reference => {
    if (reference.length < 51) {
      setDebitReference(reference)
    }
  }

  const onChangeMethod = (
    paymentMethod,
    options = { pending: false, preserveCreditLimitChecked: false },
  ) => {
    props.resetCurrentPaymentMethod()
    if (options.pending) {
      setPendingApplications(true)
      setSelectedPaymentMethod(null)
      setCard(null)
      setLimit(0)
      setDebitReference('')
      setNewCreditLimitChecked(false)
      const _selectedPendingApplication =
        props.data &&
        props.data.pendingApplications &&
        props.data.pendingApplications.applications &&
        props.data.pendingApplications.applications.length
          ? props.data.pendingApplications.applications[0]
          : null
      setSelectedPendingApplication(_selectedPendingApplication)
      setAmount(0)
      setValid(false)
      props.selectPaymentMethod({ selectedPaymentMethod: paymentMethod })
    } else {
      const coApplicant =
        paymentMethod.accounts &&
        paymentMethod.accounts.length &&
        paymentMethod.accounts[0].coApplicant
          ? paymentMethod.accounts[0].coApplicant
          : null

      if (coApplicant) {
        props.getCoApplicantInfo(coApplicant)
      }

      setSelectedPaymentMethod(paymentMethod)
      setCard(
        paymentMethod.accounts && paymentMethod.accounts.length
          ? paymentMethod.accounts[0]
          : null,
      )
      setSelectedCard(
        paymentMethod.accounts && paymentMethod.accounts.length === 1
          ? paymentMethod.accounts[0]
          : null,
      )
      setLimit(0)
      setDebitReference('')
      if (!options.preserveCreditLimitChecked) {
        setNewCreditLimitChecked(false)
      }
      setPendingApplications(false)
      setSelectedPendingApplication(null)
      setAccountNumber(getDefaultAccountNumber(paymentMethod))
      setDebitPaymentChecked(
        !props.debitDisabled &&
          (isExistingPartPayment(paymentMethod) ||
            props.debitEnabledDefault ||
            new Cookies().get('debit-payment-checked') === 'true'),
      )
      setAmount(0)
      setValid(false)

      props.selectPaymentMethod({ selectedPaymentMethod: paymentMethod })
    }
  }

  useOnUpdate(() => {
    if (props.data && props.data.paymentMethods && selectedPaymentMethod) {
      const paymentMethod = props.data.paymentMethods.find(
        _paymentMethod => _paymentMethod.id === selectedPaymentMethod.id,
      )

      if (
        !isExistingPartPayment(paymentMethod) &&
        JSON.stringify(selectedPaymentMethod) !== JSON.stringify(paymentMethod)
      ) {
        onChangeMethod(paymentMethod, { preserveCreditLimitChecked: true })
      }
    }
  }, [props.data.paymentMethods, selectedPaymentMethod])

  const onApplicationSelected = applicationId => {
    setSelectedPendingApplication(
      (props.data &&
        props.data.pendingApplications &&
        props.data.pendingApplications.applications &&
        props.data.pendingApplications.applications.find(
          _application => _application.id === applicationId,
        )) ||
        null,
    )
  }

  const onChangeLimit = _limit => {
    if (Number.isNaN(+_limit)) {
      setLimit(0)
    } else {
      setLimit(Number(_limit))
    }
  }

  const onDebitPaymentSwitch = checked => {
    if (!props.debitEnabledDefault) {
      new Cookies().set('debit-payment-checked', checked)
    }
    if (checked) {
      setAmount(0)
    }
    setDebitPaymentChecked(checked)
  }

  const onSubmit = (e, onNext) => {
    e.preventDefault()
    if (
      selectedPaymentMethod &&
      selectedPaymentMethod.type === PaymentMethods.RESURS_INVOICE
    ) {
      handleNextInvoice(onNext)
    } else {
      handleNext(onNext)
    }
  }

  const getAvailableAccounts = () =>
    (props.data &&
      props.data.pendingApplications &&
      props.data.pendingApplications.applications &&
      props.data.pendingApplications.applications.map(account => {
        const {
          id,
          approvedAmount,
          requestedAmount,
          debitableAmount,
          paymentMethodDescription,
          created,
          channelType,
        } = account

        const currency = getText(
          'default.currency',
          Number(approvedAmount || requestedAmount).toLocaleString('sv-SE', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2,
          }),
        )

        let valueLabel

        if (debitableAmount) {
          valueLabel = getText(
            `payApply.paymentMethod.pendingApplications.${getPendingApplicationLabel(
              account,
            )}`,
            paymentMethodDescription,
            currency,
            getText(
              'default.currency',
              Number(debitableAmount).toLocaleString('sv-SE', {
                maximumFractionDigits: 2,
                minimumFractionDigits: 2,
              }),
            ),
            Moment(created).format('L'),
            ApplicationChannel.labels[channelType],
          )
        } else {
          valueLabel = getText(
            `payApply.paymentMethod.pendingApplications.${getPendingApplicationLabel(
              account,
            )}`,
            paymentMethodDescription,
            currency,
            Moment(created).format('L'),
            ApplicationChannel.labels[channelType],
          )
        }

        return {
          key: id,
          value: id,
          valueLabel,
        }
      })) ||
    []

  const getCardAndCoApplicant = cardNumber => {
    const _card =
      (selectedPaymentMethod &&
        selectedPaymentMethod.accounts &&
        selectedPaymentMethod.accounts.find(
          _account =>
            _account.cardNumber === cardNumber ||
            _account.accountNumber === cardNumber,
        )) ||
      null

    if (_card && _card.coApplicant) {
      props.getCoApplicantInfo(_card.coApplicant)
    }

    return _card
  }

  const getAvailableCards = () =>
    (selectedPaymentMethod &&
      selectedPaymentMethod.accounts &&
      selectedPaymentMethod.accounts.map(_account => ({
        key: _account.cardNumber || _account.accountNumber,
        value: _account.cardNumber || _account.accountNumber,
        valueLabel: _account.existingNumber,
      }))) ||
    []

  const orderIdValidation = () => {
    if (!externalIdValid) {
      return getText('apiErrors.PAYMENT_WITH_EXTERNAL_ID_EXISTS')
    }
    return null
  }

  const isSelectedCardNeeded = () => {
    if (
      debitPaymentChecked &&
      selectedPaymentMethod &&
      selectedPaymentMethod.type === PaymentMethods.RESURS_CARD &&
      selectedPaymentMethod.customerType === CustomerTypeModel.LEGAL &&
      selectedPaymentMethod.availableCards
    ) {
      return (
        selectedPaymentMethod.availableCards.filter(
          _card => _card.cardNumber && !_card.cardNumber.includes('X'),
        ).length && selectedCard === null
      )
    }

    return false
  }

  const activePaymentMethod = () => {
    if (selectedPaymentMethod && selectedPaymentMethod.status.disabled) {
      return (
        <DisabledMethod
          onPageLoading={props.data.wizard.onPageLoading}
          handleNext={() => handleNext({ refreshPaymentMethods: true })}
          paymentMethodsExist={props.paymentMethodsExist}
          paymentMethod={selectedPaymentMethod}
          customerType={selectedPaymentMethod.customerType}
        />
      )
    }
    if (pendingApplications && selectedPendingApplication) {
      const { id, coCustomer } = selectedPendingApplication
      const { full: fullName } = (coCustomer && coCustomer.name) || {}
      const { governmentId } = coCustomer || {}
      return (
        <>
          <PendingApplications
            availableAccounts={getAvailableAccounts()}
            onApplicationSelected={val => onApplicationSelected(val)}
            selectedValue={id}
          />
          {coCustomer ? (
            <PendingApplicationCoCustomer
              name={fullName}
              governmentId={governmentId}
            />
          ) : null}
        </>
      )
    }
    if (selectedPaymentMethod) {
      const commonProps = {
        selectedPaymentMethod,
        limit,
        amount,
        newPageLoading: props.data.wizard.newPageLoading,
        isValid: validation => setValid(validation),
        onChangeLimit: _limit => onChangeLimit(_limit),
        onDebitPaymentSwitch: checked =>
          onDebitPaymentSwitch(checked && !props.debitDisabled),
        debitPaymentChecked:
          debitPaymentChecked || isExistingPartPayment(selectedPaymentMethod),
        onChangeAmount: _amount => onChangeAmount(_amount),
        customerType: props.customerType,
        key: selectedPaymentMethod._index,
      }
      switch (selectedPaymentMethod.type) {
        case PaymentMethods.RESURS_NEW_CARD:
        case !isExistingPartPayment(selectedPaymentMethod) &&
          PaymentMethods.RESURS_PART_PAYMENT:
        case PaymentMethods.RESURS_NEW_REVOLVING_CREDIT:
          return (
            <NewCardOrAccount
              {...commonProps}
              paymentMethodsExist={props.paymentMethodsExist}
              hasBonusSupport={
                props.hasBonusCheckSupport && !!props.bonusChecks.length
              }
              bonusChecks={props.bonusChecks}
              debitReference={debitReference}
              onChangeDebitReference={ref => onChangeDebitReference(ref)}
              getCostAlternatives={(_amount, paymentMethodId) => {
                props.getPaymentMethodCostAlternatives(_amount, paymentMethodId)
              }}
              costAlternatives={props.costAlternatives}
              getPaymentPerMonth={(method, _amount, months) =>
                props.getPaymentPerMonth(method, _amount, months)
              }
              monthlyCost={props.monthlyCost}
              onChangePeriodOfRepayment={period =>
                props.setPeriodOfRepayment(period)
              }
              onSendPriceInfoMail={(email, _amount) =>
                props.sendPriceInfoMail(
                  selectedPaymentMethod.id,
                  props.periodOfRepayment,
                  _amount,
                  email,
                )
              }
              debitDisabled={props.debitDisabled}
            />
          )

        case PaymentMethods.RESURS_CARD:
        case PaymentMethods.RESURS_REVOLVING_CREDIT:
        case isExistingPartPayment(selectedPaymentMethod) &&
          PaymentMethods.RESURS_PART_PAYMENT:
          return (
            <ExistingCardOrAccount
              {...commonProps}
              card={card}
              data={props.data}
              onNext={props.onNext}
              availableCards={() =>
                selectedPaymentMethod.customerType === CustomerTypeModel.LEGAL
                  ? selectedPaymentMethod.availableCards
                  : getAvailableCards()
              }
              updateCardSelected={cardNumber => {
                const _cardAndCoApplicant = getCardAndCoApplicant(cardNumber)
                setCard(_cardAndCoApplicant)
                setAccountNumber(_cardAndCoApplicant?.accountNumber)
              }}
              newCreditLimitChecked={newCreditLimitChecked}
              onCreditLimitSwitch={setNewCreditLimitChecked}
              paymentMethodsExist={props.paymentMethodsExist}
              coApplicant={props.coApplicant}
              hasBonusSupport={
                props.hasBonusCheckSupport && !!props.bonusChecks.length
              }
              bonusChecks={props.bonusChecks}
              debitReference={debitReference}
              onChangeDebitReference={ref => onChangeDebitReference(ref)}
              selectedCard={selectedCard}
              onChangeCard={cardNumber =>
                setSelectedCard(
                  (selectedPaymentMethod &&
                    selectedPaymentMethod.availableCards &&
                    selectedPaymentMethod.availableCards.length &&
                    selectedPaymentMethod.availableCards.find(
                      _card => _card.cardNumber === cardNumber,
                    )) ||
                    null,
                )
              }
              getBasisConfirmationPdf={data =>
                props
                  .getBasisConfirmationPdf(data, 'Resurs_Confirmation')
                  .then(url => url && setPdfUrl(url))
              }
              debitDisabled={props.debitDisabled}
            />
          )

        case PaymentMethods.SWISH:
        case PaymentMethods.CARD:
        case PaymentMethods.DEBIT_CARD:
        case PaymentMethods.CREDIT_CARD:
        case PaymentMethods.INTERNET:
          return (
            <PspPaymentMethod
              {...commonProps}
              debitReference={debitReference}
              onChangeDebitReference={ref => onChangeDebitReference(ref)}
              hasBonusSupport={
                props.hasBonusCheckSupport && !!props.bonusChecks.length
              }
              bonusChecks={props.bonusChecks}
            />
          )
        case PaymentMethods.RESURS_INVOICE:
        default:
          return (
            <Invoice
              {...commonProps}
              data={props.data}
              orderIdValid={externalIdValid}
              orderIdValidation={() => orderIdValidation()}
              onOrderChanged={orderData => onOrderChanged(orderData)}
              invoiceRef={props.invoiceRef}
              paymentMethodsExist={props.paymentMethodsExist}
            />
          )
      }
    } else {
      return null
    }
  }

  const handlePrevious = e => {
    e.preventDefault()
    props.onPrevious()
  }

  const buttonValidation = () => {
    switch (selectedPaymentMethod.type) {
      case PaymentMethods.RESURS_CARD:
      case PaymentMethods.RESURS_NEW_CARD:
      case PaymentMethods.RESURS_PART_PAYMENT:
      case PaymentMethods.RESURS_REVOLVING_CREDIT:
      case PaymentMethods.RESURS_NEW_REVOLVING_CREDIT:
        return true
      default:
        return false
    }
  }

  const invoiceButtonDisabled = () => {
    if (
      selectedPaymentMethod &&
      selectedPaymentMethod.type === PaymentMethods.RESURS_INVOICE
    ) {
      const totalAmount = (order && order.totalAmount) || 0
      const { minLimit, maxLimit } = selectedPaymentMethod

      return (
        !order ||
        (order && !order.orderId) ||
        totalAmount < minLimit ||
        totalAmount > maxLimit
      )
    }
    return false
  }

  if (!props.data || !props.data.paymentMethods) {
    return (
      <ErrorPage
        onNext={props.logout}
        onCancel={props.onPrevious}
        data={{
          current: {
            fatalErrorMessage: 'INVALID_GOVERNMENT_ID',
          },
        }}
      />
    )
  }

  return (
    <>
      <Conditional when={!!pdfUrl}>
        {() => (
          <PrintModalUI
            url={pdfUrl}
            show={!!pdfUrl}
            closeText={getText('default.close')}
            onClose={() => setPdfUrl(null)}
          />
        )}
      </Conditional>
      <PageWrapper title={getText('payApply.paymentMethod.title')} transparent>
        <Columns>
          <Column col12 colMd12>
            <PaymentMethodPicker
              paymentMethods={props.data.paymentMethods}
              pendingSelected={!!pendingApplications}
              hasPendingApplications={
                !!(props.data.pendingApplications.applications || []).length
              }
              onChange={(method, pending) => {
                sendPaymentOptionEvent(method, 'CHANGE')
                onChangeMethod(method, { pending })
              }}
              selectedPaymentMethod={selectedPaymentMethod}
            />
          </Column>
        </Columns>
      </PageWrapper>
      <form onSubmit={e => onSubmit(e, { refreshPaymentMethods: false })}>
        <PageWrapper>
          <Container grid960>
            <Columns>
              <Column col12 colMd12>
                <div>{activePaymentMethod()}</div>
              </Column>
            </Columns>
          </Container>
        </PageWrapper>

        <PageWrapper>
          <div
            style={{
              padding: '2rem',
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Button onClick={e => handlePrevious(e)}>
              {getText('payApply.wizard.previous')}
            </Button>
            <Button
              onClick={e => {
                if (!debitPaymentChecked || amount > 0) {
                  onSubmit(e, {})
                } else {
                  setValid(false)
                }
              }}
              form
              loading={props.data.wizard.newPageLoading}
              type="primary"
              id="payment-method-next-btn"
              disabled={
                (selectedPaymentMethod &&
                  (selectedPaymentMethod.status.disabled ||
                    (buttonValidation() && !valid) ||
                    invoiceButtonDisabled())) ||
                isSelectedCardNeeded()
              }
            >
              {getText('payApply.wizard.next')}
            </Button>
          </div>
        </PageWrapper>
      </form>
    </>
  )
}

PaymentMethodsPage.propTypes = {
  data: PropTypes.object.isRequired,
  onNext: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  coApplicant: PropTypes.object,
  getCoApplicantInfo: PropTypes.func.isRequired,
  invoiceRef: PropTypes.bool.isRequired,
  customerType: PropTypes.string.isRequired,
  getHasBonusSupport: PropTypes.func.isRequired,
  resetCurrentPaymentMethod: PropTypes.func.isRequired,
  getPaymentMethodCostAlternatives: PropTypes.func.isRequired,
  costAlternatives: PropTypes.array.isRequired,
  getPaymentPerMonth: PropTypes.func.isRequired,
  getBasisConfirmationPdf: PropTypes.func.isRequired,
  isExternalIdValid: PropTypes.bool,
  validateExternalId: PropTypes.func.isRequired,
  logout: PropTypes.func.isRequired,
  debitDisabled: PropTypes.bool.isRequired,
  paymentMethodsExist: PropTypes.bool,
}

PaymentMethodsPage.defaultProps = {
  coApplicant: null,
  isExternalIdValid: true,
  paymentMethodsExist: false,
}

const mapStateToProps = state => {
  return {
    coApplicant: state.payApply.current.coApplicant,
    governmentId: state.payApply.current.governmentId,
    invoiceRef: state.auth.hasInvoiceNumber,
    customerType: state.payApply.wizard.wizard,
    hasBonusCheckSupport: state.bonusCheck.hasBonusCheckSupport,
    bonusChecks: state.bonusCheck.bonusChecks,
    costAlternatives: state.payApply.current.costAlternatives,
    monthlyCost: state.payApply.current.monthlyCost,
    periodOfRepayment: state.payApply.current.periodOfRepayment,
    isExternalIdValid: state.payApply.current.isExternalIdValid,
    debitDisabled: state.storeInfo.debitDisabled,
    debitEnabledDefault:
      state.storeInfo.currentRepresentative.debitEnabledDefault,
  }
}

const mapDispatchToProps = dispatch => ({
  getCoApplicantInfo: id => {
    dispatch(getCoApplicantInfo(id))
  },
  getHasBonusSupport: (governmentId, customerType) => {
    dispatch(hasBonusCheckSupport(governmentId, customerType))
  },
  resetCurrentPaymentMethod: () => {
    dispatch(resetCurrentPaymentMethod())
  },
  selectPaymentMethod: paymentMethod => {
    dispatch(selectPaymentMethod(paymentMethod))
  },
  selectPendingPaymentMethod: paymentMethod => {
    dispatch(selectPendingPaymentMethod(paymentMethod))
  },
  getPaymentMethodCostAlternatives: (amount, paymentMethodId) => {
    dispatch(initialCheckPaymentMethodCostAlternatives(amount, paymentMethodId))
  },
  getPaymentPerMonth: (method, amount, months) => {
    dispatch(getPaymentPerMonth(method, amount, months))
  },
  setPeriodOfRepayment: period => {
    dispatch(setPeriodOfRepayment(period))
  },
  sendPriceInfoMail: (paymentMethodId, periodOfRepayment, amount, email) => {
    dispatch(
      sendPriceInfoMail(paymentMethodId, periodOfRepayment, amount, email),
    )
  },
  getBasisConfirmationPdf: data => dispatch(getBasisConfirmationPdf(data)),
  validateExternalId: externalId => dispatch(validateExternalId(externalId)),
  logout: () => {
    dispatch(logout())
  },
})

export default connect(mapStateToProps, mapDispatchToProps)(PaymentMethodsPage)
